import { Controller } from "@hotwired/stimulus";
import { hasModifierChord, isReservedArrowChord } from "../utils/arrow_step";
import { DetachGate } from "../utils/detach_gate";
import { ownerOf } from "../utils/event_owner";
import { closestInFlatTree, flatTreeContains } from "../utils/focus_candidate";
import { ListenerSet } from "../utils/listener_set";
import { MicrotaskCoalescer } from "../utils/microtask_coalescer";
import { MorphRenderWatcher } from "../utils/morph_render_watcher";
import { NUMBER_BOUNDS, type NumberValueConstraints } from "../utils/number_bounds";
import { NumberValueReader } from "../utils/number_value";
import { OwnedPointerSession, type OwnedPointerSessionEnd } from "../utils/owned_pointer_session";
import { TabindexLoan } from "../utils/tabindex_loan";
import { TransientHooks } from "../utils/transient_hooks";

/** The drag hook a connection may find written by an earlier, now-gone one. */
const TRANSIENT_DRAG = new TransientHooks({ attributes: ["data-dragging"] });
/** The grab hook, dropped under its own condition, so its own declaration. */
const TRANSIENT_GRAB = new TransientHooks({ attributes: ["data-grabbed"] });

/**
 * Elements whose own default action owns a key press. The browser never signals
 * those through `defaultPrevented`, so a yield on that flag cannot see them.
 */
const NATIVE_KEY_OWNERS = "input, textarea, select, button, a[href], summary, [contenteditable]";

/** How a drag session was initiated; surfaced in every event detail. */
type DragPointerType = "mouse" | "touch" | "pen" | "keyboard";

/** A pointer drag in flight (from `pointerdown` until up/cancel). */
interface PointerSession {
  /** Owns the initiating pointer; assigned as soon as the session is armed. */
  pointer: OwnedPointerSession | null;
  handle: HTMLElement;
  originX: number;
  originY: number;
  /** True once the movement passed `threshold` and `start` was dispatched. */
  started: boolean;
  /** Last axis-filtered deltas, reported again by `end`. */
  dx: number;
  dy: number;
  pointerType: DragPointerType;
}

/** A keyboard "grabbed" session (Space/Enter on the handle until drop/cancel). */
interface KeyboardSession {
  handle: HTMLElement;
  /** Cumulative synthetic deltas accumulated by the arrow keys. */
  dx: number;
  dy: number;
}

/**
 * Headless **pointer-drag primitive**: a normalized pointer/touch/mouse drag
 * lifecycle with a built-in keyboard alternative. It deliberately does NOT decide
 * what a drag *means* (reorder, dismiss, resize…) — it only emits a clean,
 * accessibility-friendly drag signal that higher-level behaviors compose from —
 * `stimeo--sortable` is one, and reordering, dismissal, and resizing are the usual
 * meanings a consumer layers on. No dedicated APG
 * pattern exists for drag-and-drop; the keyboard model below is the accepted
 * alternative-input technique (WCAG 2.1.1 / 2.5.7 Dragging Movements). Core
 * (zero dependencies).
 *
 * Markup contract (identifier: `stimeo--pointer-drag`):
 *   <li data-controller="stimeo--pointer-drag"
 *       data-stimeo--pointer-drag-axis-value="y">
 *     <button data-stimeo--pointer-drag-target="handle" aria-label="Reorder">⠿</button>
 *   </li>
 *
 * The `handle` target is optional — without one, the controller element itself
 * is the handle. Pointer flow: `pointerdown` on a handle arms the drag; once the
 * axis-filtered movement exceeds `threshold`, `start` fires, every subsequent
 * movement fires `move` (deltas cumulative from the origin), and release fires
 * `end` (`pointercancel` → `cancel`). Keyboard flow: Space/Enter on the handle
 * grabs (`start`, `data-grabbed`), the arrow keys emit synthetic `move` events of
 * `keyboardStep` px each, Space/Enter drops (`end`) and Escape or focus leaving the
 * keyboard-operable handle cancels — so every consumer gets a keyboard path for free.
 * Bare Home/End preserve a grab without moving; modified Home/End and Tab are yielded.
 * Native controls inside open shadow roots retain their editing keys and cancel a settled grab.
 * Closed shadow roots expose only their host; their editing components must stop propagation
 * of owned keys and use `reset()` when internal focus should end the surrounding grab
 * (`reset()` always ends the gesture in flight; with `follow` on it also returns the
 * element to its origin, dropping the committed follow offset and the inline `translate`,
 * and with `follow` off it leaves the inline `translate` alone).
 *
 * `start` dispatches `{ x, y, pointerType }`; `move` dispatches `{ dx, dy, x, y, pointerType }`;
 * `end` dispatches `{ dx, dy, pointerType }`; `cancel` dispatches `{ pointerType }`, also
 * when `reset()` ends a started drag or a grab (a press below the threshold ends silently).
 *
 * @remarks
 * Behavior only: consumers own all visuals (ghost, placeholder, transforms) by
 * reacting to the events and the `data-dragging` / `data-grabbed` hooks. The one
 * deliberate exception is the opt-in `follow` value for the "just move the
 * element" case: it applies the in-flight deltas to the element's CSS
 * `translate` (a property independent of `transform`, so authored transforms
 * survive), commits the offset on drop and restores it on cancel — the exact
 * boilerplate every simple consumer would otherwise re-write. With `follow` the
 * element's inline `translate` belongs to this controller. `follow` is read when a
 * gesture starts and holds until that gesture ends, so switching it mid-drag
 * describes the next gesture; the gesture's base is the committed `<x>px <y>px`
 * offset read back from the inline `translate` at that moment, so a Turbo restore,
 * a position the consumer wrote, or `follow` switched on later all continue from
 * where the element sits. ARIA 1.1
 * deprecated `aria-grabbed`/`aria-dropeffect`, so the grabbed state is
 * published as `data-grabbed` and the *meaning* of a drag must be announced by
 * the consumer (pair with `stimeo--announcer`). `touch-action` on the handles is
 * derived from `axis` (marker-guarded, authored values win) so the page does not
 * pan mid-drag. Deltas are physical (client coordinates): RTL semantics belong
 * to the consumer. `disconnect()` (Turbo navigation included) tears down any
 * active session — except across an in-page move, which the session survives
 * (the full lifecycle contract lives on `disconnect()`); `connect()` clears
 * stale hooks a Turbo cache restore may have preserved (a drag cannot survive
 * a navigation).
 */
export class PointerDragController extends Controller<HTMLElement> {
  /** Numeric read boundaries share one reader for this controller instance. */
  readonly #numbers = new NumberValueReader();

  static override targets = ["handle"];
  static override values = {
    axis: { type: String, default: "both" },
    threshold: { type: Number, default: 3 },
    keyboardStep: { type: Number, default: 10 },
    disabled: { type: Boolean, default: false },
    follow: { type: Boolean, default: false },
  };

  static valueConstraints = {
    threshold: NUMBER_BOUNDS.nonNegative,
    keyboardStep: NUMBER_BOUNDS.positive,
  } satisfies NumberValueConstraints<typeof PointerDragController.values>;
  static actions = ["reset"] as const;
  static events = ["start", "move", "end", "cancel"] as const;

  declare readonly handleTargets: HTMLElement[];
  declare readonly hasHandleTarget: boolean;
  declare axisValue: string;
  declare thresholdValue: number;
  declare keyboardStepValue: number;
  declare disabledValue: boolean;
  declare followValue: boolean;

  /** Marker attribute recording that this controller set the touch-action. */
  static readonly #TOUCH_ACTION_MARKER = "data-pointer-drag-touch-action";
  /** Keyboard handles retain their borrowed tab stop while holding focus. */
  readonly #tabindex = new TabindexLoan("0", this.identifier, { preserveFocus: true });

  #pointer: PointerSession | null = null;
  #keyboard: KeyboardSession | null = null;
  /** Whether this controller currently owns its connection resources. */
  #connected = false;
  /** The session whose focus departure is waiting for synchronous focus restoration. */
  #keyboardFocusSession: KeyboardSession | null = null;
  /** Delegated input listeners share one connected lifetime. */
  readonly #listeners = new ListenerSet();
  /** Active open shadow trees report focus pairs hidden by host retargeting. */
  readonly #shadowFocus = new ListenerSet();
  /** Settles focus after a DOM move, before the next task, without retaining old sessions. */
  readonly #keyboardFocus = new MicrotaskCoalescer(() => this.#settleKeyboardFocus());
  /** Committed follow offset the current gesture started from; its deltas add onto it. */
  #followBase = { x: 0, y: 0 };
  /**
   * Whether the gesture in flight applies its offset as a `translate`: `follow` as it
   * read when that gesture started. Consulted only while a gesture runs or ends.
   */
  #gestureFollows = false;
  /** Last offset written by this instance; absent while the inline value is only authored. */
  #followOutput: { x: number; y: number } | null = null;
  /** Decides whether a mid-session disconnect() is an in-page move or a detach. */
  readonly #gate = new DetachGate();

  readonly #morphRender = new MorphRenderWatcher(() => {
    for (const handle of this.#handles()) this.#prepareHandle(handle);
    this.#reflectSession();
  });

  override connect(): void {
    this.#connected = true;
    this.#morphRender.observe(this.element);
    if (!this.#pointer && !this.#keyboard) this.#followOutput = null;
    // A reconnect disarms the probe a mid-session disconnect() armed — the
    // element was moved in-page and the session survives (see disconnect).
    this.#gate.cancel();
    // Transient drag state cannot survive a navigation: clear hooks a Turbo
    // cache restore may have snapshotted mid-drag (idempotent reconnect). But a
    // session preserved across an in-page move keeps its hooks.
    if (!this.#pointer) TRANSIENT_DRAG.reset(this.element);
    if (!this.#keyboard) TRANSIENT_GRAB.reset(this.element);
    // Delegated on the container so dynamically added handles need no per-element
    // data-action.
    this.#listeners.add(this.element, "pointerdown", this.#onPointerDown);
    this.#listeners.add(this.element, "keydown", this.#onKeydown);
    this.#listeners.add(this.element, "focusout", this.#onFocusout);
    this.#listeners.add(this.element, "focusin", this.#onFocusin);
    this.#keyboardFocus.activate();
    if (!this.hasHandleTarget) this.#prepareHandle(this.element);
    if (this.#keyboard) {
      this.#watchShadowFocus(this.#keyboard.handle);
      this.#checkKeyboardFocus(this.#keyboard);
    }
  }

  override disconnect(): void {
    this.#connected = false;
    this.#morphRender.disconnect();
    this.#listeners.dispose();
    this.#shadowFocus.dispose();
    this.#keyboardFocus.cancel();
    this.#keyboardFocusSession = null;
    // A mid-session disconnect on a STILL-connected element is ambiguous: an
    // in-page move (a consumer like sortable re-inserts the item mid-grab —
    // Stimulus reconnects the same instance in the same mutation batch, and
    // the session must survive) vs a real detach that keeps the element
    // (`data-controller` removed, Turbo morph, element moved outside the
    // observed root). DetachGate discriminates: the token check is the
    // synchronous fast path (a morph's teardown — and its `cancel` — fires in
    // the same tick), the one-microtask probe cancelled by connect() covers
    // the rest — a detached-in-place controller never leaks its session or
    // document listeners. Probe only while a session needs protecting; an
    // idle controller tears down synchronously either way.
    if (this.#pointer || this.#keyboard) {
      this.#gate.disconnected(this, () => this.#teardown());
      return;
    }
    this.#teardown();
  }

  /**
   * Ends the gesture in flight and, with `follow` on, returns the element to its origin.
   *
   * A pointer drag or keyboard grab in flight always ends, whatever `follow` says: a
   * started drag or a grab dispatches `cancel`, a press still below the threshold ends
   * silently. With `follow` on the inline `translate` belongs to this controller, so the
   * committed follow offset and the `translate` carrying it are dropped too — the way
   * to put the element back at the start. With `follow` off the inline `translate` is
   * the author's and stays as it is. The `follow` that decides is the one the gesture
   * in flight started with, or the current declaration when no gesture is in flight.
   */
  reset(): void {
    // Read before the sessions end below: a gesture in flight answers to the `follow`
    // it started with.
    const follows = this.#pointer || this.#keyboard ? this.#gestureFollows : this.followValue;
    // Cancel first, then clear: the cancel contract snaps back to the committed
    // offset, so clearing before it would be undone on the way out.
    const live = this.#pointer?.started
      ? this.#pointer.pointerType
      : this.#keyboard
        ? "keyboard"
        : null;
    if (this.#pointer || this.#keyboard) this.#teardownSessions();
    if (live) this.#dispatchCancel(live);
    if (!follows) return;
    this.#followBase = { x: 0, y: 0 };
    this.#applyFollow(0, 0);
  }

  handleTargetConnected(handle: HTMLElement): void {
    // The element wears the handle contract only while it IS the handle; the
    // first real handle takes it back. (Marker-guarded, so this is a no-op when
    // the element never wore it.)
    if (handle !== this.element) this.#restoreHandle(this.element);
    this.#prepareHandle(handle);
  }

  /** Releases a departed declaration while preserving current handles across connection moves. */
  handleTargetDisconnected(handle: HTMLElement): void {
    // The loan is due back the moment an element stops being a handle, and this
    // callback is the only place that sees every such element: once the
    // identifier token is gone, `#handles()` no longer resolves the targets.
    // Across an in-page move `handleTargetConnected` lends it again in the same
    // mutation batch.
    this.#restoreHandle(handle);
    if (!this.#connected) return;
    if (
      this.element.contains(handle) &&
      (this.handleTargets.includes(handle) || DetachGate.isDetached(this))
    )
      return;

    const interrupted =
      this.#pointer?.handle === handle && this.#pointer.started
        ? this.#pointer.pointerType
        : this.#keyboard?.handle === handle
          ? ("keyboard" as const)
          : null;
    if (this.#pointer?.handle === handle) this.#endPointerSession();
    if (this.#keyboard?.handle === handle) this.#clearKeyboardSession();
    // With the last handle gone the element is the handle again (`#handles()`),
    // so it needs the contract the departing handle just gave back.
    if (!this.hasHandleTarget) this.#prepareHandle(this.element);
    if (interrupted) this.#dispatchCancel(interrupted);
  }

  /**
   * Re-derives the handles' touch-action when the axis changes.
   *
   * Two layers, like the `tabindex` loan: the marker says the value was once
   * ours, and the current inline value says it still is. A consumer that wrote
   * its own `touch-action` after connect keeps it.
   */
  axisValueChanged(_value: string, previousValue: string | undefined): void {
    // The value to recognize as ours is the one the previous axis lent. Stimulus
    // also replays a value that did NOT change (ahead of every connect), and
    // there the axis in force is the one it was lent under.
    const lent = this.#touchActionFor(previousValue ?? this.axisValue);
    for (const handle of this.#handles()) {
      if (!handle.hasAttribute(PointerDragController.#TOUCH_ACTION_MARKER)) continue;
      if (handle.style.touchAction !== lent) continue;
      handle.style.touchAction = this.#touchActionForAxis();
    }
  }

  /** Cancels any in-flight session when the controller is disabled mid-drag. */
  disabledValueChanged(): void {
    if (!this.disabledValue) return;
    if (this.#pointer?.started || this.#keyboard) {
      this.#dispatchCancel(this.#keyboard ? "keyboard" : (this.#pointer?.pointerType ?? "mouse"));
    }
    this.#teardownSessions();
  }

  /**
   * Arms a pointer drag on a handle; `start` waits for the threshold.
   *
   * @stimeoRuntimeOnly `disabled` decides whether this one pointer press starts a gesture.
   */
  readonly #onPointerDown = (rawEvent: Event): void => {
    const event = rawEvent as PointerEvent;
    // One session at a time: ignore a second pointerdown (multi-touch, an
    // errant tap, a grabbed keyboard session) so an in-flight drag is never
    // silently overwritten and its captured pointer orphaned.
    if (this.disabledValue || this.#pointer || this.#keyboard || event.button !== 0) return;
    const handle = this.#handleFor(event.target);
    if (!handle) return;

    // Suppress text selection / native image drag; restore the focus the
    // suppressed default would have given, so Escape and the keyboard path
    // stay reachable right after a pointer interaction (WCAG 2.1.1).
    event.preventDefault();
    handle.focus();
    this.#beginGesture();

    const session: PointerSession = {
      pointer: null,
      handle,
      originX: event.clientX,
      originY: event.clientY,
      started: false,
      dx: 0,
      dy: 0,
      pointerType: this.#pointerTypeOf(event),
    };
    // The session tracks the pointer on the handle's document, so a consumer
    // re-inserting the dragged element mid-drag (sortable's live reorder) keeps
    // steering the same finger, and every listener falls with one signal.
    session.pointer = new OwnedPointerSession(event, handle, {
      move: (move) => this.#trackMove(session, move),
      end: (kind) => this.#finishPointer(session, kind),
    });
    this.#pointer = session;
  };

  /**
   * Applies one tracked move: threshold, hooks, follow offset, events.
   *
   * @stimeoRuntimeOnly `threshold` decides when this one pointer move starts the drag.
   */
  #trackMove(session: PointerSession, event: PointerEvent): void {
    const [dx, dy] = this.#filterAxis(
      event.clientX - session.originX,
      event.clientY - session.originY,
    );
    if (!session.started) {
      if (Math.hypot(dx, dy) < this.#safeThreshold) return;
      session.started = true;
      this.element.setAttribute("data-dragging", "true");
      this.dispatch("start", {
        detail: { x: event.clientX, y: event.clientY, pointerType: session.pointerType },
      });
    }

    session.dx = dx;
    session.dy = dy;
    this.#followMove(dx, dy);
    this.dispatch("move", {
      detail: { dx, dy, x: event.clientX, y: event.clientY, pointerType: session.pointerType },
    });
  }

  /**
   * Closes the session and reports only the outcome the caller does not own.
   *
   * A teardown (detach, Escape, mid-drag disable, `reset`) is announced by the
   * path that asked for it, so this one stays silent; `pointerup` below the
   * threshold was a plain click, and `pointercancel` is an OS gesture takeover.
   */
  #finishPointer(session: PointerSession, kind: OwnedPointerSessionEnd): void {
    const { started, dx, dy, pointerType } = session;
    this.#pointer = null;
    this.element.removeAttribute("data-dragging");
    if (kind === "teardown" || !started) return;
    if (kind === "up") {
      this.#followCommit(dx, dy);
      this.dispatch("end", { detail: { dx, dy, pointerType } });
      return;
    }
    this.#dispatchCancel(pointerType);
  }

  /**
   * Keyboard alternative: Space/Enter grab & drop, arrows move, Escape cancels.
   *
   * @stimeoRuntimeOnly `disabled` decides whether this one key is taken and `keyboardStep` is the
   *   distance of one keyboard move.
   */
  readonly #onKeydown = (rawEvent: Event): void => {
    const event = rawEvent as KeyboardEvent;
    if (this.disabledValue) return;
    const handle = this.#handleFor(event.target);
    if (!handle) return;

    // A descendant widget that already claimed the key (a nested roving list, a
    // segmented field inside the handle) must not ALSO drive the drag —
    // composition depends on this yield.
    if (event.defaultPrevented) return;
    // A press that steers an IME conversion belongs to the composition, and an
    // input method never says so through `defaultPrevented`.
    if (event.isComposing) return;
    // Neither does a native control or editing surface inside the handle: typing
    // a space into a field and activating a link are default actions, not claims.
    // Widgets that do claim their keys keep composing through the yield above.
    if (this.#ownsNativeKeys((event.composedPath()[0] ?? event.target) as Element, handle)) return;
    // A chorded arrow belongs to the browser. "Someone already consumed it"
    // outranks that, so it is checked after the yields above.
    if (isReservedArrowChord(event)) return;

    // Escape cancels whichever session is live (pointer drag or keyboard grab).
    if (event.key === "Escape") {
      if (this.#pointer?.started) {
        const { pointerType } = this.#pointer;
        this.#teardownSessions();
        event.preventDefault();
        this.#dispatchCancel(pointerType);
      } else if (this.#keyboard) {
        this.#teardownSessions();
        event.preventDefault();
        this.#dispatchCancel("keyboard");
      }
      return;
    }

    if (event.key === " " || event.key === "Enter") {
      // A live pointer drag owns the session: pressing Space mid-drag (the
      // pointerdown focused the handle) must not start a parallel grab.
      if (this.#pointer) return;
      event.preventDefault();
      if (this.#keyboard) this.#dropKeyboard();
      else this.#grabKeyboard(handle);
      return;
    }

    if (!this.#keyboard) return;
    // Bare jumps preserve grabbed focus; modified Home/End yield to enclosing navigation
    // or native behavior.
    if (event.key === "Home" || event.key === "End") {
      if (hasModifierChord(event)) return;
      event.preventDefault();
      return;
    }
    const step = this.#keyboardDelta(event.key);
    if (!step) return;
    // Consume every arrow while grabbed (even on a locked axis) so the page
    // never scrolls mid-grab; only allowed-axis arrows emit a move.
    event.preventDefault();
    const [dx, dy] = this.#filterAxis(step[0], step[1]);
    if (dx === 0 && dy === 0) return;
    this.#keyboard.dx += dx;
    this.#keyboard.dy += dy;
    this.#followMove(this.#keyboard.dx, this.#keyboard.dy);
    this.dispatch("move", {
      detail: {
        dx: this.#keyboard.dx,
        dy: this.#keyboard.dy,
        x: this.#keyboard.dx,
        y: this.#keyboard.dy,
        pointerType: "keyboard",
      },
    });
  };

  /** Tracks visible internal focus before settling ownership in the same task. */
  readonly #onFocusin = (): void => {
    const session = this.#keyboard;
    if (session) {
      this.#watchShadowFocus(session.handle);
      this.#checkKeyboardFocus(session);
    }
  };

  /** A focus pair within one shadow tree does not bubble beyond its retargeted host. */
  #watchShadowFocus(handle: HTMLElement): void {
    const tree = handle.getRootNode();
    let active = (tree as Node & { readonly activeElement?: Element | null }).activeElement;
    while (active?.shadowRoot) {
      const shadow = active.shadowRoot;
      this.#shadowFocus.add(shadow, "focusin", this.#onFocusin);
      active = shadow.activeElement;
    }
  }

  /** Same-tree destinations retain ownership before the browser's focusin checkpoint. */
  readonly #onFocusout = (rawEvent: Event): void => {
    const destination = (rawEvent as FocusEvent).relatedTarget;
    const session = this.#keyboard;
    if (
      session &&
      this.#handleFor(destination) === session.handle &&
      !this.#ownsNativeKeys(destination as Element, session.handle)
    ) {
      return;
    }
    this.#checkKeyboardFocus(this.#keyboard);
  };

  /** A new session's departure replaces a completed session's pending check. */
  #checkKeyboardFocus(session: KeyboardSession | null): void {
    this.#keyboardFocusSession = session;
    this.#keyboardFocus.schedule();
  }

  /**
   * Synchronous reinsertion/refocus may keep the same operable handle after focusout.
   * Reading its own tree keeps focus resolution aligned with delegated event retargeting.
   */
  #settleKeyboardFocus(): void {
    const session = this.#keyboardFocusSession;
    this.#keyboardFocusSession = null;
    if (!session || this.#keyboard !== session || !this.element.isConnected) return;
    const tree = session.handle.getRootNode();
    const active =
      (tree as Node & { readonly activeElement?: Element | null }).activeElement ?? null;
    if (
      this.#handleFor(active) === session.handle &&
      !this.#ownsNativeKeys(active as Element, session.handle)
    ) {
      return;
    }
    this.#clearKeyboardSession();
    this.#dispatchCancel("keyboard");
  }

  /** Enters the grabbed mode: `data-grabbed` on element + handle, then `start`. */
  #grabKeyboard(handle: HTMLElement): void {
    this.#beginGesture();
    this.#keyboard = { handle, dx: 0, dy: 0 };
    this.#watchShadowFocus(handle);
    this.element.setAttribute("data-grabbed", "true");
    handle.setAttribute("data-grabbed", "true");
    this.dispatch("start", { detail: { x: 0, y: 0, pointerType: "keyboard" } });
  }

  #dropKeyboard(): void {
    const session = this.#keyboard;
    if (!session) return;
    this.#clearKeyboardSession();
    this.#followCommit(session.dx, session.dy);
    this.dispatch("end", { detail: { dx: session.dx, dy: session.dy, pointerType: "keyboard" } });
  }

  /** Maps an arrow key to a raw (unfiltered) `keyboardStep` delta. */
  #keyboardDelta(key: string): [number, number] | null {
    const step = this.#safeKeyboardStep;
    switch (key) {
      case "ArrowRight":
        return [step, 0];
      case "ArrowLeft":
        return [-step, 0];
      case "ArrowDown":
        return [0, step];
      case "ArrowUp":
        return [0, -step];
      default:
        return null;
    }
  }

  /**
   * Zeroes the delta on the locked axis (`axis` = x | y | both).
   *
   * @stimeoRuntimeOnly `axis` filters the offset of one move to the allowed direction.
   */
  #filterAxis(dx: number, dy: number): [number, number] {
    if (this.axisValue === "x") return [dx, 0];
    if (this.axisValue === "y") return [0, dy];
    return [dx, dy];
  }

  /** Silently closes a live pointer session; the caller announces the outcome. */
  #endPointerSession(): void {
    this.#pointer?.pointer?.end();
  }

  #clearKeyboardSession(): void {
    this.#shadowFocus.dispose();
    this.#keyboard?.handle.removeAttribute("data-grabbed");
    this.#keyboard = null;
    this.#keyboardFocusSession = null;
    this.element.removeAttribute("data-grabbed");
  }

  /**
   * Full teardown for a controller whose element is going away or lost its
   * controller. Into a dead (detached) tree it is silent — consumers restore
   * from their own `connect()`, not from a cancel event. But a detach that
   * KEEPS the element (Turbo morph, `data-controller` removed) leaves the
   * consumers alive and listening: an in-flight session must end in `cancel`
   * there, or a composer like sortable strands its one-at-a-time session
   * bookkeeping forever.
   */
  #teardown(): void {
    const interrupted = this.#pointer?.started
      ? this.#pointer.pointerType
      : this.#keyboard
        ? ("keyboard" as const)
        : null;
    this.#teardownSessions();
    for (const handle of this.#handles()) this.#restoreHandle(handle);
    this.#tabindex.returnAll();
    // An in-flight offset must leave the DOM even where nobody is listening: a
    // re-inserted element would have the next gesture read it back as a committed
    // base. An idle element carries no in-flight offset, and its translate is left
    // as it is.
    if (interrupted) this.#followReset();
    if (interrupted && this.element.isConnected) this.#dispatchCancel(interrupted);
  }

  /**
   * Silently tears down whatever session is live (disconnect / disabled /
   * Escape). Composed from the two single-session teardowns so pointer and
   * keyboard cleanup cannot diverge as either path evolves.
   */
  #teardownSessions(): void {
    this.#endPointerSession();
    this.#clearKeyboardSession();
  }

  #dispatchCancel(pointerType: DragPointerType): void {
    // Every cancel path funnels here, so follow's snap-back can never be missed
    // (Escape, pointercancel, mid-session disable, detach-that-keeps-the-element).
    this.#followReset();
    this.dispatch("cancel", { detail: { pointerType } });
  }

  /**
   * Reads `follow` for the gesture that is starting; with it on, takes the committed
   * offset back from the inline `translate` the element shows now.
   *
   * @stimeoRuntimeOnly `follow` decides whether this one gesture writes its offset as a translate.
   */
  #beginGesture(): void {
    this.#gestureFollows = this.followValue;
    if (this.#gestureFollows) this.#followBase = this.#parseFollowBase();
  }

  /** Applies the in-flight offset to the element's `translate` (following gestures only). */
  #followMove(dx: number, dy: number): void {
    if (!this.#gestureFollows) return;
    this.#applyFollow(this.#followBase.x + dx, this.#followBase.y + dy);
  }

  /** Folds a drop's deltas into the committed base offset (following gestures only). */
  #followCommit(dx: number, dy: number): void {
    if (!this.#gestureFollows) return;
    this.#followBase = { x: this.#followBase.x + dx, y: this.#followBase.y + dy };
    this.#applyFollow(this.#followBase.x, this.#followBase.y);
  }

  /** Snaps back to the committed position (following gestures only) — the cancel contract. */
  #followReset(): void {
    if (!this.#gestureFollows) return;
    this.#applyFollow(this.#followBase.x, this.#followBase.y);
  }

  /**
   * Repairs live-session hooks and owned follow output without advancing or ending a gesture.
   * A gesture in flight keeps the `follow` it started with; an idle element answers to the
   * current declaration.
   *
   * @stimeoRuntimeOnly `follow` gates the gesture's opt-in translate ownership; the held
   *   output is not a newly derived offset.
   */
  #reflectSession(): void {
    if (this.#pointer?.started) this.element.setAttribute("data-dragging", "true");
    else this.element.removeAttribute("data-dragging");
    if (this.#keyboard) {
      this.element.setAttribute("data-grabbed", "true");
      this.#keyboard.handle.setAttribute("data-grabbed", "true");
    } else {
      this.element.removeAttribute("data-grabbed");
      for (const handle of this.#handles()) handle.removeAttribute("data-grabbed");
    }
    const follows = this.#pointer || this.#keyboard ? this.#gestureFollows : this.followValue;
    if (follows && this.#followOutput) {
      this.#applyFollow(this.#followOutput.x, this.#followOutput.y);
    }
  }

  /**
   * Writes the offset as the standalone CSS `translate` property — independent
   * of `transform`, so an authored transform (scale, rotate…) is never
   * clobbered. At the origin the property is removed to keep the DOM clean.
   */
  #applyFollow(x: number, y: number): void {
    this.#followOutput = { x, y };
    if (x === 0 && y === 0) this.element.style.removeProperty("translate");
    else this.element.style.setProperty("translate", `${x}px ${y}px`);
  }

  /**
   * Reads a committed follow offset back from the inline `translate`. Only the
   * controller's own `<x>px <y>px` shape is recognized; anything else (an
   * authored percentage, `none`, an empty string) starts the base at 0 — follow
   * mode owns this property, per the class contract.
   */
  #parseFollowBase(): { x: number; y: number } {
    const raw = this.element.style.getPropertyValue("translate").trim();
    const match = /^(-?\d+(?:\.\d+)?)px(?:\s+(-?\d+(?:\.\d+)?)px)?$/.exec(raw);
    if (!match) return { x: 0, y: 0 };
    return { x: Number(match[1]), y: Number(match[2] ?? "0") };
  }

  /** The active handles: the `handle` targets, else the element itself. */
  #handles(): HTMLElement[] {
    return this.hasHandleTarget ? this.handleTargets : [this.element];
  }

  /** Resolves the handle owning an event target (the handle or a descendant). */
  #handleFor(target: EventTarget | null): HTMLElement | null {
    return ownerOf(this.#handles(), target);
  }

  #pointerTypeOf(event: PointerEvent): DragPointerType {
    const type = event.pointerType;
    if (type === "touch" || type === "pen") return type;
    return "mouse";
  }

  /** `touch-action` that lets the page keep panning on the locked axis only. */
  #touchActionForAxis(): string {
    return this.#touchActionFor(this.axisValue);
  }

  /** The `touch-action` an `axis` declaration lends, for any axis value. */
  #touchActionFor(axis: string): string {
    if (axis === "x") return "pan-y";
    if (axis === "y") return "pan-x";
    return "none";
  }

  /**
   * Establishes the handle contract: a `touch-action` derived from `axis`
   * (marker-guarded so an authored inline value wins and reconnects stay
   * idempotent) and focusability for the keyboard path (`tabindex="0"` only when
   * the author supplied none — prefer a real `<button>` handle). Both additions
   * are marker-owned so `#restoreHandle` reverts them symmetrically on teardown.
   *
   * @stimeoRenderRoot
   */
  #prepareHandle(handle: HTMLElement): void {
    const lent = this.#touchActionForAxis();
    // Two layers, as when the loan is returned: a free property is ours to take,
    // and a marked one is still ours only while it reads back as what was lent.
    // A consumer that wrote its own value keeps it across every reconnect.
    const marked = handle.hasAttribute(PointerDragController.#TOUCH_ACTION_MARKER);
    if (handle.style.touchAction === "" || (marked && handle.style.touchAction === lent)) {
      handle.style.touchAction = lent;
      handle.setAttribute(PointerDragController.#TOUCH_ACTION_MARKER, "true");
    }
    this.#tabindex.lend(handle, { onlyIfUnfocusable: true });
  }

  /** Native key ownership follows visible shadow focus and its flat-tree ancestry. */
  #ownsNativeKeys(target: Element, handle: HTMLElement): boolean {
    let element = target;
    while (element.shadowRoot?.activeElement) element = element.shadowRoot.activeElement;
    const owner = closestInFlatTree(element, NATIVE_KEY_OWNERS);
    return owner !== null && owner !== handle && flatTreeContains(handle, owner);
  }

  /** Reverts the marker-owned touch-action + tabindex (authored values untouched). */
  #restoreHandle(handle: HTMLElement): void {
    if (handle.hasAttribute(PointerDragController.#TOUCH_ACTION_MARKER)) {
      // Only give back what is still ours: a consumer may have written its own
      // value since, and clearing then would lose it. The marker goes either
      // way — the loan is over regardless of who ends up owning the value.
      if (handle.style.touchAction === this.#touchActionForAxis()) {
        handle.style.touchAction = "";
      }
      handle.removeAttribute(PointerDragController.#TOUCH_ACTION_MARKER);
    }
    this.#tabindex.return(handle);
  }
  /** Current `threshold` declaration resolved against its numeric contract. */
  get #safeThreshold(): number {
    return this.#numbers.read(
      this,
      "threshold",
      this.thresholdValue,
      PointerDragController.values.threshold.default,
      PointerDragController.valueConstraints.threshold,
    );
  }

  /** Current `keyboardStep` declaration resolved against its numeric contract. */
  get #safeKeyboardStep(): number {
    return this.#numbers.read(
      this,
      "keyboardStep",
      this.keyboardStepValue,
      PointerDragController.values.keyboardStep.default,
      PointerDragController.valueConstraints.keyboardStep,
    );
  }
}
